<script>
import Aws from '@/services/Aws';
import { required } from "vuelidate/lib/validators";
import validationMessages from '@/components/validations';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
    components: {
        flatPickr,
        validationMessages
    },
    props: ['applicationsData'],
    watch: {
        applicationsData(newVal) {
            if (newVal) {
                this.applications = newVal;
            }
        }
    },
    data() {
        return {
            csrf_token: localStorage.getItem('csrf_token'),
            submitted: false,
            showModal: false,
            tryingToSubmit: false,
            applications: [],
            application_id: '',
            start_at: '',
            selectedActions: ['update_auto_scaling_group', 'read_db_instance'],
            actionOptions: [
                { text: 'Application Servers', value: 'update_auto_scaling_group' },
                { text: 'Database Reader Instances', value: 'read_db_instance' },
            ],
            datetimeconfig: {
                enableTime: true,
                time_24hr: true,
                dateFormat: "Y-m-d H:i",
                minDate: new Date(),
                minuteIncrement: 5
            },
        };
    },
    validations: {
        application_id: { required },
        start_at: { required },
        selectedActions: {
            required(value) {
                return Array.isArray(value) && value.length > 0;
            }
        },
    },
    methods: {
        async scheduleScaleDown() {
            this.tryingToSubmit = true;
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.tryingToSubmit = false;
                return;
            }

            try {
                const params = {
                    application_id: this.application_id,
                    start_at: this.start_at,
                    actions: this.selectedActions,
                    csrf_token: this.csrf_token,
                };

                const response = await Aws.scheduleScaleDown(params);
                const payload = response.data.data || {};
                const errors = payload.errors || {};

                if (payload.success) {
                    const scheduled = (payload.scheduled_actions || []).join(', ');
                    let message = 'Scale down scheduled successfully.';
                    if (scheduled) {
                        message += ` Actions: ${scheduled}.`;
                    }
                    if (Object.keys(errors).length) {
                        message += ` Some actions failed.`;
                    }
                    this.successmsg(message);
                    this.closeModal();
                    this.refreshData();
                } else {
                    this.failedmsg(payload.error || 'Failed to schedule scale down');
                }
            } catch (error) {
                const err = error.response?.data?.error
                    ? error.response.data.error
                    : 'Failed to schedule scale down';
                this.failedmsg(err);
            } finally {
                this.tryingToSubmit = false;
            }
        },

        refreshData() {
            this.$emit('onRefresh');
        },

        closeModal() {
            this.$bvModal.hide('schedule_scale_down');
        },

        resetForm() {
            this.submitted = false;
            this.tryingToSubmit = false;
            this.application_id = '';
            this.start_at = '';
            this.selectedActions = ['update_auto_scaling_group', 'read_db_instance'];
        },
    }
};
</script>

<template>
    <b-modal
        id="schedule_scale_down"
        v-model="showModal"
        title="Schedule Scale Down"
        title-class="font-18"
        centered
        @hidden="resetForm"
    >
        <form @submit.prevent="scheduleScaleDown">
            <div class="row">
                <div class="col-sm-12">
                    <label class="form-label mb-2">Start At</label>
                    <flat-pickr
                        v-model="start_at"
                        :config="datetimeconfig"
                        class="form-control mb-2"
                        :class="{ 'is-invalid': submitted && $v.start_at.$error }"
                        name="start_at"
                    />
                    <validationMessages v-if="submitted" :fieldName="'Start At'" :validationName="$v.start_at"></validationMessages>
                </div>

                <div class="col-sm-12">
                    <b-form-group label="Select Application" label-for="scale-down-application">
                        <select
                            id="scale-down-application"
                            v-model="application_id"
                            :class="{ 'is-invalid': submitted && $v.application_id.$error }"
                            class="form-select"
                        >
                            <option :value="''">Please select application</option>
                            <option
                                v-for="application in applications"
                                :key="application.id"
                                :value="application.id"
                            >
                                {{ application.name }}
                            </option>
                        </select>
                        <validationMessages v-if="submitted" :fieldName="'Application'" :validationName="$v.application_id"></validationMessages>
                    </b-form-group>
                </div>

                <div class="col-sm-12">
                    <b-form-group label="Actions to scale down">
                        <b-form-checkbox-group
                            v-model="selectedActions"
                            :options="actionOptions"
                            stacked
                            :class="{ 'is-invalid': submitted && $v.selectedActions.$error }"
                        />
                        <validationMessages v-if="submitted" :fieldName="'Actions'" :validationName="$v.selectedActions"></validationMessages>
                    </b-form-group>
                </div>
            </div>
        </form>

        <template v-slot:modal-footer>
            <b-button variant="light" @click="closeModal" :disabled="tryingToSubmit">Close</b-button>
            <b-button variant="primary" @click="scheduleScaleDown" :disabled="tryingToSubmit">
                <b-spinner v-show="tryingToSubmit" small></b-spinner>
                Schedule
            </b-button>
        </template>
    </b-modal>
</template>
